// Timestamp parsing — shared by the console and the widget.
//
// D1 writes `CURRENT_TIMESTAMP` as UTC in the form `2026-08-02 17:36:00`, with NO timezone
// marker. A space-separated string like that is not ISO-8601, and browsers parse it as LOCAL
// time — so for anyone west of UTC the value lands in the FUTURE. Observed live 2026-08-02:
// a scan from four hours earlier rendered as "-1d ago", and an older one as "today".
//
// Everything that shows a time must go through here.

/** Parse a timestamp from the API, treating a zone-less value as UTC (which is what D1 stores). */
export function parseApiTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const s = String(value).trim();
  // Already carries a zone (ISO with Z or ±hh:mm) → trust it.
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  const iso = hasZone ? s : `${s.replace(' ', 'T')}Z`;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * Coarse relative label. Clamps at zero: a timestamp that still resolves slightly ahead (clock
 * skew between the browser and the edge) reads "just now" rather than a nonsensical negative.
 */
export function timeAgo(value: string | null | undefined, opts?: { never?: string }): string {
  const t = parseApiTime(value);
  if (t == null) return opts?.never ?? '—';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

/** Absolute, human, in the viewer's own timezone. */
export function formatApiTime(value: string | null | undefined, fallback = '—'): string {
  const t = parseApiTime(value);
  return t == null ? fallback : new Date(t).toLocaleString();
}

/** Date only. `opts` is passed through to `toLocaleDateString` (e.g. `{ month:'short', day:'numeric' }`). */
export function formatApiDate(
  value: string | null | undefined,
  fallback = '—',
  opts?: Intl.DateTimeFormatOptions,
): string {
  const t = parseApiTime(value);
  return t == null ? fallback : new Date(t).toLocaleDateString(undefined, opts);
}
